// src/pages/Details.jsx
import React, { useEffect, useState, useMemo } from 'react'
import Headers from '../components/Headers'
import Footer from '../components/Footer'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { MdOutlineKeyboardArrowRight, MdOutlineShare, MdOutlineFavorite, MdOutlineFavoriteBorder } from 'react-icons/md'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { useDispatch, useSelector } from 'react-redux'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { Pagination, Navigation, Autoplay } from 'swiper'
import Ratings from '../components/Ratings'
import { AiOutlineShoppingCart, AiOutlineHeart, AiFillHeart } from 'react-icons/ai'
import { FaFacebookF, FaLinkedin, FaWhatsapp, FaTwitter, FaFemale } from 'react-icons/fa'
import Reviews from '../components/Reviews'
import {
  get_product,
  get_reviews,
  fetchMoreProductsBySeller,
  add_viewed_product
} from '../store/reducers/homeReducer'
import { add_to_card, messageClear, add_to_wishlist } from '../store/reducers/cardReducer'
import { add_cart_activity, add_buy_activity } from '../store/reducers/homeReducer'
import toast from 'react-hot-toast'
import { fetchVtoAssets, clearVtoState } from '../store/reducers/vtoReducer'
import VtoModal from '../components/VtoModal'
import SameSellerProduct from '../components/products/SameSellerProduct'
import { addSwapItem } from '../store/reducers/virtualSwapReducer'
import { add_swap_activity } from '../store/reducers/homeReducer'
import '../styles/commonfeminine.css'

const Details = () => {
  const navigate = useNavigate()
  const { productId } = useParams()
  const dispatch = useDispatch()

  const {
    product,
    relatedProducts,
    moreProducts
  } = useSelector((state) => state.home)
  const { totalReview, rating_review } = useSelector(s => s.home)

  const { userInfo } = useSelector((state) => state.auth)
  const { errorMessage, successMessage } = useSelector((state) => state.card)
  const { assets, loading: vtoLoading } =
    useSelector((state) => state.vto || { assets: [], loading: false })

  // Loading state management
  const loaderList = useMemo(
    () => ['/images/Loader1.mp4', '/images/Loader2.mp4', '/images/Loader3.mp4'],
    []
  );
  const [loaderSrc] = useState(
    () => loaderList[Math.floor(Math.random() * loaderList.length)]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);

  const LOADER_WIDTH = '150px';
  const LOADER_HEIGHT = '200px';
  const LOADER_BORDER_RADIUS = '50px';
  const VIDEO_PLAYBACK_RATE = 2.2;
  const MIN_LOADER_TIME_MS = 2000;

  // Local state
  const [selectedImage, setSelectedImage] = useState('')
  const [activeTab, setActiveTab] = useState('reviews')
  const [quantity, setQuantity] = useState(1)
  const [vtoOpen, setVtoOpen] = useState(false)
  const [currentEffectUrl, setUrl] = useState('')
  const [isWishlisted, setIsWishlisted] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  // Loading management useEffects
  useEffect(() => {
    document.body.style.overflow = isLoading ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoading]);

  // Reset loading when productId changes
  useEffect(() => {
    setIsLoading(true);
    setIsDataLoaded(false);
    setMinTimeElapsed(false);
    setCurrentProductId(productId);
    setSelectedImage('');
    setQuantity(1);
  }, [productId]);

  // Minimum loading time
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), MIN_LOADER_TIME_MS);
    return () => clearTimeout(timer);
  }, [currentProductId]);

  // Check if data is loaded and minimum time elapsed
  useEffect(() => {
    if (isDataLoaded && minTimeElapsed) {
      setIsLoading(false);
    }
  }, [isDataLoaded, minTimeElapsed]);

  // Fetch product details
  useEffect(() => {
    async function fetchProductData() {
      try {
        await dispatch(get_product(productId));
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Error fetching product:', error);
        setIsDataLoaded(true);
      }
    }
    if (productId) {
      fetchProductData();
    }
  }, [productId, dispatch]);

  // Fetch reviews and more products once we have product data
  useEffect(() => {
    if (product?._id) {
      dispatch(get_reviews(product._id));
      dispatch(fetchMoreProductsBySeller(product.sellerId));
    }
  }, [product?._id, dispatch])

  // Set initial selected image
  useEffect(() => {
    if (product?.images?.length && !selectedImage) {
      setSelectedImage(product.images[0]);
    }
  }, [product?.images, selectedImage]);

  // Toasts
  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage)
      dispatch(messageClear())
    }
    if (successMessage) {
      toast.success(successMessage)
      dispatch(messageClear())
    }
  }, [errorMessage, successMessage, dispatch])

  // Track view
  useEffect(() => {
    if (userInfo?.id && productId && productId.trim()) {
      dispatch(add_viewed_product({
        userId: userInfo.id,
        productId: productId.trim()
      }))
    }
  }, [userInfo?.id, productId, dispatch])

  const add_card = () => {
    if (!userInfo) return navigate('/login')
    dispatch(add_to_card({
      userId: userInfo.id,
      quantity,
      productId: product._id
    }))
    dispatch(add_cart_activity({
      userId: userInfo.id,
      productId: product._id
    }))
    toast.success('Added to cart!')
  }

  const add_wishlist = () => {
    if (!userInfo) return navigate('/login')
    dispatch(add_to_wishlist({
      userId: userInfo.id,
      productId: product._id,
      name: product.name,
      price: product.price,
      image: product.images[0],
      discount: product.discount,
      rating: product.rating
    }))
    setIsWishlisted(!isWishlisted)
    toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist!')
  }

  const buy = () => {
    if (!userInfo) return navigate('/login')
    const basePrice = product.discount
      ? product.price - Math.floor((product.price * product.discount)/100)
      : product.price

    const order = [{
      sellerId: product.sellerId,
      shopName: product.shopName,
      price: quantity * (basePrice - Math.floor((basePrice * 5)/100)),
      products: [{ quantity, productInfo: product }]
    }]

    dispatch(add_buy_activity({
      userId: userInfo.id,
      productId: product._id
    }))

    navigate('/shipping', {
      state: {
        products: order,
        price: basePrice * quantity,
        shipping_fee: 85,
        items: 1
      }
    })
  }

  // Compute average rating
  const averageRating = useMemo(() => {
    if (!rating_review?.length || totalReview === 0) {
      return product.rating || 0
    }
    let totalCount = 0
    let totalSum = 0
    rating_review.forEach((bin, i) => {
      const count = bin.sum || 0
      const ratingVal = 5 - i
      totalCount += count
      totalSum += ratingVal * count
    })
    return (totalSum / totalCount).toFixed(1)
  }, [rating_review, totalReview, product.rating])

  const handleTryOn = (e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    
    dispatch(addSwapItem({
      userId: userInfo.id,
      productId: product._id,
      slug: product.slug,
      name: product.name,
      price: product.price,
      discount: product.discount,
      image: product.images[0],
      category: product.category,
      shopName: product.shopName,
    }));
    
    // Track swap activity
    dispatch(add_swap_activity({ userId: userInfo.id, productId: product._id }));
    
    // Navigate to virtual swap page
    navigate('/virtual-swap');
  }

  const inc = () => {
    if (quantity >= product.stock) toast.error('Out of stock')
    else setQuantity(q => q + 1)
  }
  
  const dec = () => {
    if (quantity > 1) setQuantity(q => q - 1)
  }

  const shareProduct = (platform) => {
    const url = window.location.href;
    const text = `Check out this amazing product: ${product.name}`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    
    window.open(shareUrls[platform], '_blank');
    setShowShareModal(false);
  };

  // Show loading screen
  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center" style={{ background: 'var(--color-bg)' }}>
        <div style={{ width: LOADER_WIDTH, height: LOADER_HEIGHT, overflow: 'hidden', borderRadius: LOADER_BORDER_RADIUS }}>
          <video
            autoPlay
            loop
            muted
            preload="none"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onLoadedMetadata={e => (e.currentTarget.playbackRate = VIDEO_PLAYBACK_RATE)}
          >
            <source src={loaderSrc} type="video/mp4" />
          </video>
        </div>
      </div>
    );
  }

  // --- Product Not Found/Error Handling ---
  const isProductInvalid = !product || !product._id || typeof product.stock === 'undefined' || !product.images || !Array.isArray(product.images);
  if (isProductInvalid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-gray-800">
        <Headers />
        <div className="flex-1 flex flex-col items-center justify-center py-20">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Product Not Found</h2>
          <p className="mb-6 text-gray-600">The product you are looking for does not exist or could not be loaded.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded bg-primary text-white font-semibold shadow hover:bg-accent transition-colors"
            style={{ background: 'var(--color-primary)' }}
          >
            Go Back
          </button>
          <button
            onClick={() => navigate('/')}
            className="mt-3 px-6 py-3 rounded border font-semibold text-primary border-primary hover:bg-primary hover:text-white transition-colors"
            style={{ color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
          >
            Go to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const discountedPrice = product.discount
    ? product.price - Math.floor((product.price * product.discount)/100)
    : product.price;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <Headers />

      {/* Hero Banner */}
      <div className="relative h-[180px] mt-1 overflow-hidden top-4">
        <img
          src={`https://www.kollybollyethnics.com/image/catalog/data/14Dec2022/Bollywood-model-pista-green-sequins-georgette-saree-nc5201.jpg`}
          alt="Order Banner"
          className="absolute w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
        <div className="absolute left-0 top-0 w-full h-full flex items-center justify-center">
          <h2 className="text-4xl font-extrabold text-white tracking-widest drop-shadow-lg">
            Elite Collections
          </h2>
        </div>
      </div>

      {/* Mobile-First Product Layout */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Breadcrumb */}
        <nav className="mb-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <div className="flex items-center space-x-2 overflow-x-auto">
            <Link to="/" className="hover:text-primary transition-colors whitespace-nowrap">Home</Link>
            <MdOutlineKeyboardArrowRight className="flex-shrink-0" />
            <Link to="/" className="hover:text-primary transition-colors whitespace-nowrap">{product.category}</Link>
            <MdOutlineKeyboardArrowRight className="flex-shrink-0" />
            <span className="whitespace-nowrap" style={{ color: 'var(--color-text-primary)' }}>{product.name}</span>
          </div>
        </nav>

        {/* Main Product Section - Mobile First */}
        <div className="space-y-6">
          
          {/* Image Gallery - Cool Carousel */}
          <div className="w-full">
            {/* Main Image Carousel */}
            <div className="card overflow-hidden mb-4">
              <div className="aspect-square relative">
                <Swiper
                  slidesPerView={1}
                  spaceBetween={0}
                  loop={true}
                  autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                  }}
                  pagination={{
                    clickable: true,
                    dynamicBullets: true,
                  }}
                  navigation={true}
                  modules={[Pagination, Navigation, Autoplay]}
                  className="product-image-swiper"
                >
                  {product.images?.map((img, index) => (
                    <SwiperSlide key={index}>
                      <div className="relative w-full h-full">
                        <img
                          src={img}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {/* Wishlist Button */}
                        <button
                          onClick={add_wishlist}
                          className="absolute top-3 right-3 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10"
                          style={{ 
                            background: 'var(--color-card-bg)',
                            color: isWishlisted ? 'var(--color-primary)' : 'var(--color-text-secondary)'
                          }}
                        >
                          {isWishlisted ? <AiFillHeart size={20} /> : <AiOutlineHeart size={20} />}
                        </button>
                        
                        {/* Discount Badge */}
                        {product.discount > 0 && (
                          <div 
                            className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-bold text-white z-10"
                            style={{ background: 'var(--color-primary)' }}
                          >
                            -{product.discount}%
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {product.images?.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto hide-scrollbar pb-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === img 
                        ? 'border-primary' 
                        : 'border-transparent hover:border-accent'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info - Mobile Optimized */}
          <div className="space-y-4 sm:space-y-6">
            {/* Product Title & Rating */}
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                {product.name}
              </h1>
              
              <div className="flex items-center justify-center sm:justify-start space-x-4 mb-4">
                <div className="flex items-center">
                  <Ratings ratings={parseFloat(averageRating)} />
                  <span className="ml-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    ({totalReview} reviews)
                  </span>
                </div>
                
                <button
                  onClick={() => setShowShareModal(true)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <MdOutlineShare size={18} />
                </button>
              </div>
            </div>

            {/* Price - Centered on Mobile */}
            <div className="text-center sm:text-left space-y-2">
              {product.discount > 0 ? (
                <div className="flex items-baseline justify-center sm:justify-start space-x-3">
                  <span className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
                    ₹{discountedPrice}
                  </span>
                  <span className="text-base sm:text-lg line-through" style={{ color: 'var(--color-text-secondary)' }}>
                    ₹{product.price}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold px-2 py-1 rounded" style={{ 
                    background: 'var(--color-accent)', 
                    color: 'var(--color-text-primary)' 
                  }}>
                    {product.discount}% OFF
                  </span>
                </div>
              ) : (
                <span className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  ₹{product.price}
                </span>
              )}
            </div>

            {/* Color Selection */}
            {product.color && (
              <div className="flex items-center justify-center sm:justify-start space-x-3">
                <span className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>Color:</span>
                {(() => {
                  const [name, hex] = product.color.split(' - ').map(s => s.trim())
                  return (
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-white shadow-md"
                        style={{ backgroundColor: hex || name }}
                      />
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{name}</span>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Quantity Selector - Centered on Mobile */}
            {product.stock > 0 && (
              <div className="space-y-3 text-center sm:text-left">
                <span className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>Quantity:</span>
                <div className="flex items-center justify-center sm:justify-start space-x-4">
                  <div className="flex items-center border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                    <button 
                      onClick={dec}
                      className="px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      –
                    </button>
                    <span className="px-3 sm:px-4 py-2 font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {quantity}
                    </span>
                    <button 
                      onClick={inc}
                      className="px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      +
                    </button>
                  </div>
                  
                  <span className="text-xs sm:text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {product.stock} items available
                  </span>
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className={`text-sm font-semibold text-center sm:text-left ${product.stock ? 'text-green-600' : 'text-red-600'}`}>
              {product.stock ? `✓ In Stock` : '✗ Out of Stock'}
            </div>

            {/* Action Buttons - All in Same Column */}
            <div className="space-y-3 px-4 sm:px-0">
              {product.stock > 0 ? (
                <>
                  <button
                    onClick={add_card}
                    className="w-full py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 active:scale-95"
                    style={{ 
                      background: 'var(--color-primary)',
                      color: '#fff',
                      border: '2px solid var(--color-primary)'
                    }}
                  >
                    <AiOutlineShoppingCart className="inline mr-2" />
                    Add to Cart
                  </button>
                  
                  <button
                    onClick={buy}
                    className="w-full py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg border-2 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                    style={{ 
                      borderColor: 'var(--color-primary)', 
                      color: 'var(--color-primary)',
                      background: 'transparent'
                    }}
                  >
                    Buy Now
                  </button>

                  <button
                    onClick={handleTryOn}
                    className="w-full py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg border-2 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                    style={{ 
                      borderColor: 'var(--color-accent)', 
                      color: 'var(--color-accent)',
                      background: 'transparent'
                    }}
                  >
                    <FaFemale className="inline mr-2" />
                    Try It On
                  </button>
                </>
              ) : (
                <button
                  disabled
                  className="w-full py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg bg-gray-300 text-gray-500 cursor-not-allowed"
                >
                  Out of Stock
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="card mt-8">
          <div className="border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex space-x-8 justify-center sm:justify-start">
              {['reviews', 'description'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 font-semibold text-sm border-b-2 transition-colors capitalize ${
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-secondary hover:text-primary'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          
          <div className="p-4 sm:p-6">
            {activeTab === 'reviews' ? (
              <Reviews product={product} />
            ) : (
              <div className="prose max-w-none text-center sm:text-left" style={{ color: 'var(--color-text-secondary)' }}>
                <p className="leading-relaxed">{product.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Same Seller Products */}
        <div className="mt-8">
          <h3 className="text-lg sm:text-xl font-bold mb-4 text-center sm:text-left" style={{ color: 'var(--color-text-primary)' }}>
            More from {product.shopName}
          </h3>
          <SameSellerProduct
            moreProducts={moreProducts}
            shopName={product.shopName}
          />
        </div>

        {/* Related Products */}
        <div className="mt-8">
          <h3 className="text-lg sm:text-xl font-bold mb-4 text-center sm:text-left" style={{ color: 'var(--color-text-primary)' }}>
            You might also like
          </h3>
          <Swiper
            slidesPerView={1}
            spaceBetween={16}
            breakpoints={{
              480: { slidesPerView: 2, spaceBetween: 20 },
              768: { slidesPerView: 3, spaceBetween: 24 },
              1024: { slidesPerView: 4, spaceBetween: 24 },
            }}
            pagination={{ clickable: true }}
            navigation={true}
            modules={[Pagination, Navigation]}
            className="related-products-swiper"
          >
            {relatedProducts.map((p, i) => (
              <SwiperSlide key={i}>
                <Link to={`/product/details/${p._id}`} className="block">
                  <div className="card overflow-hidden group">
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        src={p.images[0]}
                        alt={p.name}
                      />
                      {p.discount > 0 && (
                        <div 
                          className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold text-white"
                          style={{ background: 'var(--color-primary)' }}
                        >
                          -{p.discount}%
                        </div>
                      )}
                    </div>
                    <div className="p-3 sm:p-4">
                      <h4 className="font-semibold mb-2 line-clamp-2 text-sm sm:text-base" style={{ color: 'var(--color-text-primary)' }}>
                        {p.name}
                      </h4>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm sm:text-base" style={{ color: 'var(--color-primary)' }}>
                          ₹{p.discount ? p.price - Math.floor((p.price * p.discount)/100) : p.price}
                        </span>
                        <Ratings ratings={parseFloat(p.rating)} />
                      </div>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4 text-center" style={{ color: 'var(--color-text-primary)' }}>
              Share this product
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: 'Facebook', icon: FaFacebookF, platform: 'facebook', color: '#1877F2' },
                { name: 'Twitter', icon: FaTwitter, platform: 'twitter', color: '#1DA1F2' },
                { name: 'WhatsApp', icon: FaWhatsapp, platform: 'whatsapp', color: '#25D366' },
                { name: 'LinkedIn', icon: FaLinkedin, platform: 'linkedin', color: '#0077B5' }
              ].map(({ name, icon: Icon, platform, color }) => (
                <button
                  key={platform}
                  onClick={() => shareProduct(platform)}
                  className="flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border transition-all duration-200 hover:scale-105"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <Icon size={18} style={{ color }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {name}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowShareModal(false)}
              className="w-full mt-4 py-2 px-4 rounded-lg border transition-colors"
              style={{ 
                borderColor: 'var(--color-border)', 
                color: 'var(--color-text-secondary)' 
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <VtoModal open={vtoOpen} effectUrl={currentEffectUrl} onClose={() => setVtoOpen(false)} />
      <Footer />
    </div>
  )
}

export default Details
